import React, { useEffect, useState } from "react";
import Popover from "@material-ui/core/Popover";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import Typography from "@material-ui/core/Typography";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import { useDispatch, useSelector } from "react-redux";
import { fetchLoggedUserChains } from "state/logged_user_chains";
import { State } from "state";
import { Chain } from "interfaces/chain";
import { RouteComponentProps, withRouter } from "react-router-dom";
import default_user from "../assets/img/default_user.jpg";
import ChainParentDefaultLogo from "../assets/img/ChainParentDefaultLogo.png";
import swapProfile from "../assets/img/swap-profile.svg";
import Modal from "./LightThemeModal";
import UserFilter from "../containers/UserFilter";
import axios from "axios";
import Config from "../config";
import { getHeaders } from "../helpers/headers";
import { getItem, setItem, removeItem } from "../helpers/storage";
import { IMPERSONATIONTOKEN, IMPERSONATIONUSER } from "../state/auth";
import Constants from "../helpers/constants";
import Loader from "./Loader";
import { SeverityLevel, ToastService } from "../services/toastService";
import { fetchChainProjects } from "state/logged_user_chains";
import { encryptProjectId } from "helpers/encryptUrlId";

interface ProfileDropdownProps {
  open: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  onClick: (chain: Chain) => void;
  name: string;
  email: string;
  authUserRole: string;
}

type Props = ProfileDropdownProps & RouteComponentProps;

const ProfileDropdown: React.FC<Props> = ({
  open,
  onClose,
  anchorEl,
  history,
  onClick,
  name,
  email,
  authUserRole,
}) => {
  const dispatch = useDispatch();
  const { content: chains } = useSelector((state: State) => state.logged_user_chains);

  const { chainProjects, projectsLoading } = useSelector((state: State) => state.logged_user_chains);
  
  // cache for fetched parent chain logos (keyed by chain id)
  const [parentLogos, setParentLogos] = useState<Record<number, string | null>>({});
const hasHandledProject = React.useRef(false);
  const [switchUserModalState, setSwitchUserModalState] = useState({
    open: false,
    isAwaitingDelete: false,
    isContinueBtnDisplay: true,
    loading: false,
  });
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [selectedChain, setSelectedChain] = useState<Chain | null>(null);
  // Detect mobile
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 540;

  useEffect(() => {
    const impersonationToken = getItem(IMPERSONATIONTOKEN);
    setIsImpersonating(!!impersonationToken);
    dispatch(fetchLoggedUserChains());
  }, [dispatch, IMPERSONATIONTOKEN]);

  useEffect(() => {
    const unlisten = history.listen(() => {
      onClose();
    });
    return unlisten;
  }, [history, onClose]);


  const handleChainClick = (chain: Chain) => {
    hasHandledProject.current = false; // reset
    setSelectedChain(chain);
    dispatch(fetchChainProjects(chain.id));
  };
  useEffect(() => {
    if (
      !projectsLoading &&
      chainProjects &&
      selectedChain &&
      !hasHandledProject.current
    ) {
      hasHandledProject.current = true; // prevent re-run

      if (chainProjects.length === 1) {
        const project = chainProjects[0];
        const roleId = 2;

        const projectId = encryptProjectId(Number(project.project_id));

        history.push(`/projects-new/${projectId}/${roleId}`);
        onClose();
      } else {
        onClick(selectedChain); // open modal
      }
    }
  }, [chainProjects, projectsLoading, selectedChain]);

  // Prepare chain parent logo URLs for chains that don't already provide a logo.
  // Use the image proxy endpoint directly; the <img> has an onError fallback to the default.
  useEffect(() => {
    if (!chains || chains.length === 0) return;

    chains.forEach((chain) => {
      const id = (chain as any).id as number | undefined;
      if (!id) return;

      // skip if already have an entry (could be null meaning no logo)
      if (parentLogos[id] !== undefined) return;

      // if chain already has logo value, store it and skip constructing proxy URL
      if ((chain as any).logo) {
        setParentLogos((p) => ({ ...p, [id]: (chain as any).logo }));
        return;
      }

      // Construct direct image proxy URL; the proxy will return 404 if no logo exists.
      setParentLogos((p) => ({ ...p, [id]: `${Config.API_SERVICE_URL}/api/chain-parent-logo/${id}/image` }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chains]);


  const handleSwitchUser = async (user: any) => {
    if (!user) return;
    setSwitchUserModalState((p) => ({ ...p, loading: true }));
    try {
      const response = await axios.post(
        `${Config.API_SERVICE_URL}/api/userImpersonate/start`,
        JSON.stringify({ email: user.user.email }),
        getHeaders({ "Content-Type": "application/json" })
      );
      const { impersonationToken, targetUser } = response.data;
      setIsImpersonating(true);
      setItem(IMPERSONATIONTOKEN, impersonationToken);
      setItem(IMPERSONATIONUSER, JSON.stringify(targetUser));
      window.dispatchEvent(new Event("impersonation-updated"));
      window.location.pathname === "/associated-chains"
        ? window.location.reload()
        : history.push("/associated-chains");
    } catch (error: any) {
      setIsImpersonating(false);
      const message = error?.response?.data?.message || "Failed to switch user. Please try again.";
      ToastService.getInstance().publishToast(
        { identifier: "error-operation", title: "Message", message },
        SeverityLevel.ERROR,
        { autoClose: 3000 }
      );
    } finally {
      setSwitchUserModalState((p) => ({ ...p, loading: false }));
    }
  };

  const handleReturnToMyAccount = async () => {
    const impersonationToken = getItem(IMPERSONATIONTOKEN);
    setSwitchUserModalState((p) => ({ ...p, loading: true }));
    await axios
      .post(
        `${Config.API_SERVICE_URL}/api/userImpersonate/stop`,
        JSON.stringify({ impersonationToken }),
        getHeaders()
      )
      .catch((error) => {
        const message = error?.response?.data?.message || "Failed to switch user. Please try again.";
        ToastService.getInstance().publishToast(
          { identifier: "error-operation", title: "Message", message },
          SeverityLevel.ERROR,
          { autoClose: 3000 }
        );
      })
      .finally(() => {
        setSwitchUserModalState((p) => ({ ...p, loading: false }));
        removeItem(IMPERSONATIONTOKEN);
        removeItem(IMPERSONATIONUSER);
        window.location.pathname === "/associated-chains"
          ? window.location.reload()
          : history.push("/associated-chains");
      });
  };

  // ── Styles ──────────────────────────────────────────────
  const lineSeparatorCss: React.CSSProperties = {
    height: "1px",
    width: "100%",
    backgroundColor: "#53598D26",
    margin: "5px auto",
  };
  const profileCardStyle: React.CSSProperties = {
    padding: "0px",
    display: "flex",
    alignItems: "start",
    gap: "10px",
  };
  const swapProfileCss: React.CSSProperties = {
    padding: "0px",
    display: "flex",
    alignItems: "start",
    gap: "10px",
  };
  const userImageStyle: React.CSSProperties = {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    border: "1px solid #cdcdcd",
  };
  const userNameStyle: React.CSSProperties = {
    fontWeight: 400,
    padding: 0,
    margin: 0,
    lineHeight: "16px",
    fontFamily: "poppins",
    wordBreak: "break-word",
  };
  const userEmailStyle: React.CSSProperties = {
    padding: 0,
    margin: 0,
    fontSize: "14px",
    wordBreak: "break-all",
  };
  const swapProfileTextCss: React.CSSProperties = {
    padding: "0px",
    margin: 0,
    fontSize: "12px",
    cursor: "pointer",
    paddingTop: "2%",
    color: "#374151",
  };
  const containerStyle: React.CSSProperties = {
    padding: "0px",
    display: "flex",
    flexDirection: "column",
    height: authUserRole === Constants.AB_SYSTEM_ADMIN ? "300px" : "auto",
    overflowY: "auto",
  };
  const listItemBaseStyle: React.CSSProperties = { padding: "12px 0px" };
  const logOutIconStyle: React.CSSProperties = { padding: "12px 16px", justifyContent: "end" };
  const logoStyle: React.CSSProperties = { height: "35px", width: "35px", objectFit: "contain", flexShrink: 0 };
  const logoutIconStyle: React.CSSProperties = { color: "#E5B42F" };
  const viewAllStyle: React.CSSProperties = {
    color: "#E5B42F",
    textDecoration: "underline",
    fontSize: "14px",
    fontWeight: 400,
    padding: "0px 16px 8px 0px",
    marginBottom: "0px",
    cursor: "pointer",
    display: "block",
    textAlign: "center",
  };

  // Desktop paper style — unchanged from original
  const desktopPaperStyle: React.CSSProperties = {
    width: "24%",
    height: 565,
    marginTop: 55,
    borderRadius: 16,
    left: "80%",
  };

  // Mobile paper style — full-width, positioned from top
  const mobilePaperStyle: React.CSSProperties = {
    width: "calc(100vw - 24px)",
    maxWidth: "100vw",
    height: "auto",
    maxHeight: "85vh",
    marginTop: 55,
    borderRadius: 16,
    left: "12px",
    right: "12px",
    overflowY: "auto",
  };

  return (
    <>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          className: "api-table-body-wrapper",
          style: {
            width: "80vw",
            maxWidth: 350,
            maxHeight: 565,
            height: authUserRole === Constants.AB_SYSTEM_ADMIN? "calc(100vh - 90px)" : "auto",
            marginTop: 55,
            borderRadius: 16,
            left:"80%",
            position: "fixed",
            overflowY: "auto",
          },
        }}
      >
        <div style={{ padding: 16 }}>
          {/* User Profile */}
          <div style={profileCardStyle}>
            <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              <p style={userNameStyle}>{name}</p>
              <p style={userEmailStyle}>{email}</p>
            </div>
          </div>

          <div style={lineSeparatorCss} />

          {/* Swap Profile */}
          {authUserRole === Constants.AB_SYSTEM_ADMIN && (
            <>
            <div style={swapProfileCss}>
              <img src={swapProfile} style={userImageStyle} alt="swap" />
              {!isImpersonating ? (
                <p
                  style={{ ...swapProfileTextCss, cursor: "pointer", fontWeight: 600 }}
                  onClick={() => setSwitchUserModalState((p) => ({ ...p, open: true }))}
                >
                  Operate as user
                </p>
              ) : (
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}
                  onClick={handleReturnToMyAccount}
                >
                  <p
                    style={{
                      ...swapProfileTextCss,
                      fontWeight: 600,
                      color: "#3c6df1",
                      margin: 0,
                      paddingTop: "7%",
                    }}
                  >
                    Switch back to my account
                  </p>
                </div>
              )}
              <div style={{ paddingTop: "2%" }}>
                {switchUserModalState.loading && <Loader height={20} width={20} custom />}
              </div>
            </div>
            <div style={lineSeparatorCss} />
            </>
          )}

          

          {/* Chains List */}
          <div style={containerStyle}>
            <List disablePadding>
              {chains
                ?.slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                ?.map((chain, index) => (
                  <ListItem
                    onClick={() => handleChainClick(chain)}
                    button
                    key={index}
                    style={listItemBaseStyle}
                  >
                    <img
                      src={(parentLogos as any)[chain.id] || (chain as any).logo || ChainParentDefaultLogo}
                      alt={`${chain.name} Logo`}
                      style={logoStyle}
                      onError={(e) => {
                        const img = e.currentTarget as HTMLImageElement;
                        if (img.src !== ChainParentDefaultLogo) img.src = ChainParentDefaultLogo;
                      }}
                    />
                    <p
                      style={{
                        textDecoration: "underline",
                        color: "#374151",
                        fontSize: "14px",
                        fontWeight: 200,
                        paddingLeft: "20px",
                        margin: 0,
                        wordBreak: "break-word",
                      }}
                    >
                      {chain.name}
                    </p>
                  </ListItem>
                ))}
            </List>
          </div>

          {/* View All */}
          {chains && chains.length > 0 && (
            <>
            <div>
              <a
                href="/associated-chains"
                onClick={(e) => { e.preventDefault(); history.push("/associated-chains"); }}
                style={viewAllStyle}
              >
                View All
              </a>
            </div>
          <div style={lineSeparatorCss} />
          </>
          )}

          {/* Logout */}
          <ListItem
            button
            onClick={() => { handleReturnToMyAccount(); history.push("/logout"); }}
            style={logOutIconStyle}
          >
            <ExitToAppIcon style={logoutIconStyle} />
            <Typography style={{ marginLeft: 8 }}>Logout</Typography>
          </ListItem>
        </div>
      </Popover>

      {/* Swap User Modal */}
      <Modal
        show={switchUserModalState.open}
        onClose={() => setSwitchUserModalState((p) => ({ ...p, open: false }))}
        title="Operate as User"
        width={isMobile ? "90vw" : "420px"}
        subText="Select a user to operate with their permissions and access."
        closeOnBackdropClick={false}
        overflow="visible"
        minHeight="441px"
      >
        <div style={{ marginTop: "5%" }}>
          <UserFilter onChange={(u) => handleSwitchUser(u)} />
        </div>
        {switchUserModalState.loading && (
          <div style={{ paddingTop: "40px" }}>
            <Loader height={70} width={70} custom />
          </div>
        )}
      </Modal>
    </>
  );
};

export default withRouter(ProfileDropdown);